/**
 * Pack hall rules (F7.1 – F7.4, F7.8): planned pouches, the minimum pouch weight and the spot
 * weight schedule, the first / last pouch check, code reconciliation and carton labels. Pure.
 */
import { isSameIstDay } from '@/lib/ist'
import type { Grams } from '@/lib/weight'
import type { PackLot } from '@/offline/sitePack'

/** Kilos × 1000 ÷ pack size, rounded down (F7.1). */
export function plannedPouches(plannedG: Grams, netG: Grams): number {
  if (netG <= 0) return 0
  return Math.floor(plannedG / netG)
}

/** Below net − tolerance is underweight and must be emptied and counted as rejected (F7.3). */
export function minPouchWeight(netG: Grams, toleranceG: Grams): Grams {
  return netG - toleranceG
}

export function isBelowMinimum(valueG: number, netG: Grams, toleranceG: Grams): boolean {
  return valueG < minPouchWeight(netG, toleranceG)
}

/**
 * The pouch number at which the next spot weight is asked: every `every` pouches (the 1st pouch
 * too), skipping positions already weighed. Null while none is due.
 */
export function spotWeightDue(
  pouchesFilled: number,
  every: number,
  weighedPositions: readonly number[],
): number | null {
  if (pouchesFilled <= 0 || every <= 0) return null
  const done = new Set(weighedPositions)
  const due = [1]
  for (let p = every; p <= pouchesFilled; p += every) due.push(p)
  for (const p of due) if (p <= pouchesFilled && !done.has(p)) return p
  return null
}

/** Share of spot weights below minimum, in percent (0 when none typed). */
export function lowShare(weights: readonly number[], netG: Grams, toleranceG: Grams): number {
  if (!weights.length) return 0
  const low = weights.filter((w) => isBelowMinimum(w, netG, toleranceG)).length
  return (low * 100) / weights.length
}

export type PouchCheck = 'MATCH' | 'MISMATCH' | 'SACK_TAG'

/**
 * First / last pouch (F7.3): the decoded QR text must equal the lot's QR link character for
 * character. A text that is one of our sack or bag tags is a sack tag, not a pouch code.
 */
export function checkPouchText(
  scanned: string,
  qrUrl: string,
  isKnownTag: (code: string) => boolean,
): PouchCheck {
  if (scanned === qrUrl) return 'MATCH'
  if (isKnownTag(scanned)) return 'SACK_TAG'
  return 'MISMATCH'
}

export const PACK_LOT_CODE_RE = /^PL-\d{4}-\d{4}-\d{4}-[A-HJ-NP-Z2-9]$/

/** The pack lot code inside a pouch or carton QR link (both GS1 and interim shapes), or null. */
export function lotCodeFromQr(text: string): string | null {
  const t = text.trim()
  const m = /^https:\/\/id\.uncompromised\.in\/(?:01\/\d{14}\/)?10\/([A-Za-z0-9-]+)\/?$/i.exec(t)
  const code = (m ? m[1]! : t).toUpperCase()
  return PACK_LOT_CODE_RE.test(code) ? code : null
}

export interface Reconciliation {
  printed: number
  applied: number
  spoiled: number
  destroyed: number
  /** printed − (applied + spoiled + destroyed); 0 when it adds up. */
  gap: number
  ok: boolean
}

/** Codes printed = pouches applied (good) + spoiled + destroyed (F7.4). */
export function reconcile(c: {
  printed: number
  applied: number
  spoiled: number
  destroyed: number
}): Reconciliation {
  const gap = c.printed - (c.applied + c.spoiled + c.destroyed)
  return { ...c, gap, ok: gap === 0 }
}

/** Carton labels: full cartons plus one partial carton with its own count (F7.4). */
export function cartonSplit(pouches: number, perCarton: number): { full: number; partial: number } {
  if (perCarton <= 0) return { full: 0, partial: pouches }
  return { full: Math.floor(pouches / perCarton), partial: pouches % perCarton }
}

/** Fill data after midnight of the pack date is late (F7.3): the phone forces Filling finished. */
export function fillDayOver(packDate: string, now: number = Date.now()): boolean {
  return !isSameIstDay(`${packDate.slice(0, 10)}T12:00:00+05:30`, now)
}

export type DispatchRefusal = 'NOT_RELEASED' | 'ON_HOLD' | 'TOO_MANY' | 'NONE_LEFT'

/** Pouches of a lot still at the hall. */
export function pouchesLeft(lot: Pick<PackLot, 'pouch_count' | 'pouches_dispatched'>): number {
  return Math.max(0, (lot.pouch_count ?? 0) - lot.pouches_dispatched)
}

/** Only released lots not on hold, and never more pouches than are left (F7.8). */
export function checkDispatchLot(
  lot: Pick<PackLot, 'status' | 'hold' | 'pouch_count' | 'pouches_dispatched'>,
  pouches: number | null,
): DispatchRefusal | null {
  if (lot.hold !== 'NONE') return 'ON_HOLD'
  if (lot.status !== 'RELEASED') return 'NOT_RELEASED'
  const left = pouchesLeft(lot)
  if (left === 0) return 'NONE_LEFT'
  if (pouches !== null && pouches > left) return 'TOO_MANY'
  return null
}

export type LotState = 'PACKING' | 'WAITING' | 'RELEASED' | 'HELD' | 'RECALLED' | 'WITHDRAWN'

/** What the hall sees for a pack lot: the hold first, then the status. */
export function lotState(lot: Pick<PackLot, 'status' | 'hold'>): LotState {
  if (lot.hold === 'RECALLED') return 'RECALLED'
  if (lot.hold === 'HELD') return 'HELD'
  if (lot.status === 'RELEASED') return 'RELEASED'
  if (lot.status === 'WITHDRAWN' || lot.status === 'DESTROYED') return 'WITHDRAWN'
  if (lot.status === 'PACKING') return 'PACKING'
  return 'WAITING'
}
